/**
 * Formulario de creación/edición de Contratos (presentacional).
 * Extraído de AdminPanel (App.js) sin cambios de comportamiento.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

const CATALOGO_CABINA = { 2: 2699, 3: 3299, 4: 3799, 5: 4699 };
const CATALOGO_360 = { 2: 3299, 3: 3899, 4: 4499, 5: 4999 };
const CATALOGO_KEY_MOMENTS = { 80: 2999, 100: 3250, 140: 3499, 200: 4499 };

export function ContractForm({
  contractForm, setContractForm, editingContractId, cancelEdit,
  createContract, updateContract, contractPreview, DatePicker,
}) {
  return (
    <Card className="card-premium border-gold/30">
      <CardHeader>
        <CardTitle className="text-pearl flex justify-between items-center">
          <span>{editingContractId ? `Editando: ${contractForm.event_name}` : 'Nuevo Contrato'}</span>
          <Button variant="ghost" size="sm" onClick={cancelEdit} className="text-pearl-muted">✕</Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tipo de Contrato */}
        <div className="flex gap-4 p-3 bg-night/50 rounded">
          <div className="flex items-center gap-2">
            <input type="radio" name="contract_type" checked={contractForm.contract_type === "public"}
              onChange={() => setContractForm({ ...contractForm, contract_type: "public", special_price: null })} />
            <Label className="text-pearl">Contrato Público (Precios Netos)</Label>
          </div>
          <div className="flex items-center gap-2">
            <input type="radio" name="contract_type" checked={contractForm.contract_type === "special"}
              onChange={() => setContractForm({ ...contractForm, contract_type: "special" })} />
            <Label className="text-gold">Contrato Proveedor/Especial</Label>
          </div>
        </div>

        {/* Datos del Cliente - SOLO Nombre y Teléfono */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-pearl">Nombre Cliente *</Label>
            <Input value={contractForm.client_name} onChange={(e) => setContractForm({ ...contractForm, client_name: e.target.value })} className="input-premium" />
          </div>
          <div>
            <Label className="text-pearl">Teléfono *</Label>
            <Input value={contractForm.client_phone} onChange={(e) => setContractForm({ ...contractForm, client_phone: e.target.value })} className="input-premium" />
          </div>
        </div>

        {/* Salón */}
        <div>
          <Label className="text-pearl">Salón / Lugar del Evento</Label>
          <Input placeholder="Ej: Salón Imperial, Quinta Los Pinos..." value={contractForm.salon} onChange={(e) => setContractForm({ ...contractForm, salon: e.target.value })} className="input-premium" />
        </div>

        {/* Fecha y Horarios */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-pearl">Fecha Evento *</Label>
            <DatePicker value={contractForm.event_date} onChange={(event_date) => setContractForm({ ...contractForm, event_date })} placeholder="Seleccionar fecha" />
          </div>
          <div>
            <Label className="text-pearl">Horario Evento</Label>
            <Input placeholder="ej: 6:00 PM" value={contractForm.event_time} onChange={(e) => setContractForm({ ...contractForm, event_time: e.target.value })} className="input-premium" data-testid="contract-event-time-input" />
          </div>
          <div>
            <Label className="text-pearl">Hora de inicio del servicio</Label>
            <Input placeholder="ej: 7:00 PM" value={contractForm.service_time} onChange={(e) => setContractForm({ ...contractForm, service_time: e.target.value })} className="input-premium" data-testid="contract-service-time-input" />
          </div>
        </div>
        <div>
          <Label className="text-pearl">Comentarios del horario (opcional)</Label>
          <Textarea
            placeholder="Ej: Después del vals o según lo indiquen"
            value={contractForm.service_time_comment || ""}
            onChange={(e) => setContractForm({ ...contractForm, service_time_comment: e.target.value })}
            className="input-premium"
            rows={2}
            maxLength={350}
            data-testid="contract-service-time-comment-input"
          />
          <p className="text-pearl-muted text-xs mt-1">Se imprimirá en negrita debajo de la Hora de Inicio en el contrato ({(contractForm.service_time_comment || "").length}/350)</p>
        </div>

        {/* Autorización de publicación de fotos */}
        <div className="p-3 bg-night/50 border border-gold/20 rounded-lg">
          <Label className="text-pearl font-bold text-sm">Autorización de publicación</Label>
          <Select value={contractForm.publication_authorization || "pendiente"} onValueChange={(v) => setContractForm({ ...contractForm, publication_authorization: v })}>
            <SelectTrigger className="bg-night border-gold/30 text-pearl mt-1" data-testid="contract-publication-authorization-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pendiente">Pendiente (el cliente marca al firmar)</SelectItem>
              <SelectItem value="si">Sí autoriza publicar la galería</SelectItem>
              <SelectItem value="no">NO autoriza publicar</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-pearl-muted text-xs mt-1">Controla la casilla marcada en la Cláusula 6 del contrato</p>
        </div>

        {/* === TOTAL Y SALDO EN TIEMPO REAL === */}
        <div className="p-4 bg-gold/20 border border-gold/40 rounded-lg space-y-3">
          <div className="flex justify-between items-center gap-4">
            <Label className="text-gold font-bold">TOTAL NETO:</Label>
            <div className="flex items-center gap-2">
              <span className="text-gold">$</span>
              <Input
                type="number"
                placeholder="Auto o manual"
                value={contractForm.manual_total || ((contractForm.price_cabina || 0) + (contractForm.price_video360 || 0) + (contractForm.price_key_moments || 0) + (contractForm.price_live || 0)) || ""}
                onChange={(e) => setContractForm({ ...contractForm, manual_total: parseInt(e.target.value) || 0 })}
                className="input-premium w-32 text-right text-xl font-bold"
              />
              <span className="text-gold font-bold">MXN</span>
            </div>
          </div>
          <p className="text-pearl-muted/60 text-xs">* Suma automática de servicios. Puedes sobrescribir manualmente.</p>

          <div className="flex justify-between items-center gap-4">
            <Label className="text-red-400">Descuento o Promoción:</Label>
            <div className="flex items-center gap-2">
              <span className="text-red-400">-$</span>
              <Input
                type="number"
                placeholder="0"
                value={contractForm.discount_amount || ""}
                onChange={(e) => setContractForm({ ...contractForm, discount_amount: parseInt(e.target.value) || 0 })}
                className="input-premium w-32 text-right"
              />
              <span className="text-red-400">MXN</span>
            </div>
          </div>

          <div className="flex justify-between items-center gap-4">
            <Label className="text-pearl-muted">Anticipo recibido:</Label>
            <div className="flex items-center gap-2">
              <span className="text-pearl-muted">$</span>
              <Input
                type="number"
                placeholder="0"
                value={contractForm.anticipo_amount || ""}
                onChange={(e) => setContractForm({ ...contractForm, anticipo_amount: parseInt(e.target.value) || 0 })}
                className="input-premium w-32 text-right"
              />
              <span className="text-pearl-muted">MXN</span>
            </div>
          </div>

          {(() => {
            // Si hay TOTAL NETO manual, ya viene CON el descuento aplicado (no se le vuelve a restar) y
            // el Subtotal de referencia = Total + Descuento (igual que el PDF).
            // Si NO hay TOTAL NETO manual, el total sale de la suma de servicios MENOS el descuento,
            // igual que lo calcula el backend (net_price = subtotal - discount_amount).
            const rawSum = (contractForm.price_cabina || 0) + (contractForm.price_video360 || 0) + (contractForm.price_key_moments || 0) + (contractForm.price_live || 0);
            const total = contractForm.manual_total || Math.max(0, rawSum - (contractForm.discount_amount || 0));
            const subtotal = contractForm.manual_total ? total + (contractForm.discount_amount || 0) : rawSum;
            const saldo = total - (contractForm.anticipo_amount || 0);
            return (
              <>
                {contractForm.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-pearl-muted">Subtotal (sin descuento):</span>
                    <span className="text-pearl">${subtotal.toLocaleString()} MXN</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-gold/30">
                  <span className="text-gold font-bold text-lg">SALDO:</span>
                  <span className={`text-2xl font-black ${saldo <= 0 ? 'text-green-400' : 'text-pearl'}`}>
                    ${Math.max(0, saldo).toLocaleString()} MXN
                  </span>
                </div>
                {saldo <= 0 && total > 0 && (
                  <div className="p-2 bg-green-500/20 border border-green-500/50 rounded text-center">
                    <span className="text-green-400 font-bold">✓ SERVICIO TOTALMENTE LIQUIDADO</span>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* === CATÁLOGO DE SERVICIOS (4 ÚNICOS) === */}
        <div className="p-4 bg-night/50 border border-gold/20 rounded-lg space-y-4">
          <Label className="text-gold font-bold text-sm block">SERVICIOS A CONTRATAR (Precios NETO)</Label>

          <div className="p-3 bg-night/30 rounded">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox checked={contractForm.include_cabina} onCheckedChange={(c) => setContractForm({ ...contractForm, include_cabina: c, cabina_hours: c ? 2 : 0, price_cabina: c ? CATALOGO_CABINA[2] : 0 })} />
              <Label className="text-gold font-bold">Cabina de Fotos</Label>
            </div>
            {contractForm.include_cabina && (
              <Select value={contractForm.cabina_hours.toString()} onValueChange={(v) => setContractForm({ ...contractForm, cabina_hours: parseInt(v), price_cabina: CATALOGO_CABINA[parseInt(v)] })}>
                <SelectTrigger className="bg-night border-gold/30 text-gold">
                  <SelectValue placeholder="Selecciona horas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 horas - $2,699 NETO</SelectItem>
                  <SelectItem value="3">3 horas - $3,299 NETO</SelectItem>
                  <SelectItem value="4">4 horas - $3,799 NETO</SelectItem>
                  <SelectItem value="5">5 horas - $4,699 NETO</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="p-3 bg-night/30 rounded">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox checked={contractForm.include_video360} onCheckedChange={(c) => setContractForm({ ...contractForm, include_video360: c, video360_hours: c ? 2 : 0, price_video360: c ? CATALOGO_360[2] : 0 })} />
              <Label className="text-gold font-bold">Video 360</Label>
            </div>
            {contractForm.include_video360 && (
              <Select value={contractForm.video360_hours.toString()} onValueChange={(v) => setContractForm({ ...contractForm, video360_hours: parseInt(v), price_video360: CATALOGO_360[parseInt(v)] })}>
                <SelectTrigger className="bg-night border-gold/30 text-gold">
                  <SelectValue placeholder="Selecciona horas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 horas - $3,299 NETO</SelectItem>
                  <SelectItem value="3">3 horas - $3,899 NETO</SelectItem>
                  <SelectItem value="4">4 horas - $4,499 NETO</SelectItem>
                  <SelectItem value="5">5 horas - $4,999 NETO</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="p-3 bg-night/30 rounded">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox checked={contractForm.include_key_moments} onCheckedChange={(c) => setContractForm({ ...contractForm, include_key_moments: c, key_moments_pieces: c ? 80 : 0, price_key_moments: c ? CATALOGO_KEY_MOMENTS[80] : 0 })} />
              <Label className="text-gold font-bold">Key Moments</Label>
            </div>
            {contractForm.include_key_moments && (
              <Select value={contractForm.key_moments_pieces.toString()} onValueChange={(v) => setContractForm({ ...contractForm, key_moments_pieces: parseInt(v), price_key_moments: CATALOGO_KEY_MOMENTS[parseInt(v)] })}>
                <SelectTrigger className="bg-night border-gold/30 text-gold">
                  <SelectValue placeholder="Selecciona piezas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="80">80 piezas - $2,999 NETO</SelectItem>
                  <SelectItem value="100">100 piezas - $3,250 NETO</SelectItem>
                  <SelectItem value="140">140 piezas - $3,499 NETO</SelectItem>
                  <SelectItem value="200">200 piezas - $4,499 NETO</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="p-3 bg-night/30 rounded">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox checked={contractForm.include_live} onCheckedChange={(c) => setContractForm({ ...contractForm, include_live: c, price_live: c ? 700 : 0 })} />
                <Label className="text-gold font-bold">PicPartyLive</Label>
              </div>
              <span className="text-gold text-sm">$700 NETO</span>
            </div>
            {contractForm.include_live && (
              <p className="text-gold/80 text-xs mt-2 p-2 bg-gold/10 rounded">
                Internet y pantallas corren por cuenta del anfitrion.
              </p>
            )}
          </div>
        </div>

        {/* Precio Especial (solo si es contrato especial) */}
        {contractForm.contract_type === "special" && (
          <div className="p-3 bg-gold/10 border border-gold/30 rounded">
            <Label className="text-gold font-bold">Precio Especial (Convenio)</Label>
            <Input type="number" placeholder="Ingresa el precio final acordado" value={contractForm.special_price || ""} onChange={(e) => setContractForm({ ...contractForm, special_price: parseInt(e.target.value) || null })} className="input-premium mt-2" />
          </div>
        )}

        {/* Notas */}
        <div>
          <Label className="text-pearl">Notas adicionales</Label>
          <Textarea value={contractForm.notes} onChange={(e) => setContractForm({ ...contractForm, notes: e.target.value })} className="input-premium" rows={2} />
        </div>

        {/* Cortesía / Regalo */}
        <div className="p-3 bg-gold/10 border border-gold/30 rounded">
          <Label className="text-gold font-bold text-sm">Cortesía / Regalo</Label>
          <Input
            placeholder="Ej: Foto impresa 20x30, Marco de madera, etc."
            value={contractForm.cortesia || ""}
            onChange={(e) => setContractForm({ ...contractForm, cortesia: e.target.value })}
            className="input-premium mt-1"
          />
          <p className="text-pearl-muted text-xs mt-1">Se imprimirá en el contrato como cortesía incluida</p>
        </div>

        {/* === APARTADO DE PAGOS (USO INTERNO) === */}
        <div className="p-4 bg-night/70 border border-pearl-muted/20 rounded-lg">
          <h4 className="text-pearl-muted font-bold text-sm mb-3 flex items-center gap-2">
            <span className="w-5 h-5 bg-pearl-muted/20 rounded flex items-center justify-center text-xs">$</span>
            USO INTERNO
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-pearl-muted text-xs">Estado del Pago</Label>
              <Select value={contractForm.anticipo_status} onValueChange={(v) => setContractForm({ ...contractForm, anticipo_status: v })}>
                <SelectTrigger className="bg-night border-pearl-muted/30 text-pearl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="abonado">Abonado</SelectItem>
                  <SelectItem value="pagado">PAGADO - Liquidado</SelectItem>
                  <SelectItem value="dia_evento">Día del Evento</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-pearl-muted text-xs">Costo Proveedor</Label>
              <Input
                type="number"
                placeholder="Tu costo real"
                value={contractForm.costo_proveedor || ""}
                onChange={(e) => setContractForm({ ...contractForm, costo_proveedor: parseInt(e.target.value) || null })}
                className="input-premium mt-1"
              />
              {contractForm.costo_proveedor && contractForm.manual_total > 0 && (
                <p className="text-gold text-xs mt-1">
                  Utilidad: ${(contractForm.manual_total - contractForm.costo_proveedor).toLocaleString()}
                </p>
              )}
            </div>
          </div>

          {contractForm.anticipo_status === "pagado" && (
            <div className="mt-3 p-3 bg-green-500/20 border border-green-500/50 rounded">
              <div className="flex items-center gap-2">
                <Label className="text-green-400 text-xs">Fecha de Liquidación</Label>
                <div className="w-48">
                  <DatePicker
                    value={contractForm.fecha_pago || new Date().toISOString().split('T')[0]}
                    onChange={(fecha_pago) => setContractForm({ ...contractForm, fecha_pago })}
                    placeholder="Fecha pago"
                  />
                </div>
              </div>
              <p className="text-green-400 text-sm font-bold mt-2">✓ SERVICIOS LIQUIDADOS</p>
            </div>
          )}
        </div>

        {/* Botones */}
        <div className="flex gap-3">
          {editingContractId ? (
            <>
              <Button onClick={cancelEdit} className="btn-gold-outline">
                Cancelar
              </Button>
              <Button onClick={updateContract} className="btn-gold">
                Guardar Cambios
              </Button>
            </>
          ) : (
            <Button onClick={createContract} className="btn-gold">
              Crear Contrato
            </Button>
          )}
        </div>

        {/* Preview del cálculo */}
        {contractPreview && (
          <div className="p-4 bg-gold/10 border border-gold/30 rounded">
            <div className="flex justify-between text-pearl">
              <span>Subtotal:</span>
              <span>${contractPreview.subtotal.toLocaleString()} NETO</span>
            </div>
            {contractForm.discount_amount > 0 && (
              <div className="flex justify-between text-gold">
                <span>Descuento:</span>
                <span>-${contractForm.discount_amount.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between text-gold font-bold text-xl mt-2 pt-2 border-t border-gold/30">
              <span>PRECIO NETO:</span>
              <span>${contractPreview.netPrice.toLocaleString()}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
